import type { Context, Lijst } from '@xxtract/tabelfilters';
import { formatInvoicePeriod } from './format';
import type { Invoice } from './invoices.api';

/** Periode filters on its shown label (month and year), which depends on the locale. */
export interface FactuurContext extends Context {
  locale: string;
}

const STATUSSEN = ['paid', 'open', 'unknown'] as const;

/**
 * Kolommen van facturen (spec-tabelfilters story 5), gedeeld door pagina en tabel; selectie en PDF staan
 * erbuiten. Status was eerst een tabblad; een oude link `?status=paid` blijft werken (`open` omvat vervallen).
 */
export const FACTUUR_LIJST: Lijst<Invoice, FactuurContext> = {
  kolommen: [
    { sleutel: 'nummer', kopSleutel: 'invoices.table.invoiceNumber', type: 'tekst', waarde: (f) => f.invoiceNumber },
    { sleutel: 'datum', kopSleutel: 'invoices.table.invoiceDate', type: 'datum', waarde: (f) => f.invoiceDate },
    {
      sleutel: 'periode',
      kopSleutel: 'invoices.table.period',
      type: 'tekst',
      waarde: (f, c) => (f.period != null ? formatInvoicePeriod(f.period, f.invoiceDate, c.locale) : null),
      // The label is a month name: sorting it would be alphabetical ('april' before 'januari'); datum sorts chronologically.
      sorteer: false,
    },
    { sleutel: 'omschrijving', kopSleutel: 'invoices.table.description', type: 'tekst', waarde: (f) => f.description },
    { sleutel: 'bedrag', kopSleutel: 'invoices.table.amount', type: 'getal', waarde: (f) => f.amountInclTax },
    {
      sleutel: 'status',
      kopSleutel: 'invoices.table.status',
      type: 'keuzelijst',
      waarde: (f) => f.status,
      waardeLabel: (w, t) => t(`invoices.status.${w}`),
      vasteWaarden: STATUSSEN,
    },
  ],
};

export const FACTUREN_SLEUTEL = 'facturen';
/** Paginafilter: the chosen year, under its own key in the list state. */
export const JAAR_SLEUTEL = 'facturen.jaar';

/**
 * The year as a mini list with one choice column, so an old link `?year=` goes through the same reader.
 * It also reads `sort`, so it carries the invoice columns (unfilterable) to accept exactly the same sorts.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- only the column descriptions count here
export function jaarLijst(jaren: readonly number[]): Lijst<any, any> {
  return {
    kolommen: [
      ...FACTUUR_LIJST.kolommen.map((k) => ({ ...k, filter: false })),
      {
        sleutel: 'jaar',
        kopSleutel: 'invoices.filters.yearLabel',
        type: 'keuzelijst',
        waarde: (j) => String(j),
        urlNaam: 'year',
        enkelvoudig: true,
        vasteWaarden: jaren.map(String),
      },
    ],
  };
}
